import {apiFetch, uploadAppealReceipt} from "../client";
import type {OutPaymentResponse, ReceiptUploadResponse} from "../client";

// Disputes domain (admin)

/** Один загруженный к платежу файл: id, ссылка на скачивание и момент загрузки. */
export interface DisputeReceipt {
    receiptId: string;
    receiptUrl: string | null;
    uploadedAt: string;
}

export interface AppealItem {
    externalCompletion?: import("../../generated/openapi-contract").ExternalCompletion;
    payinParts?: import("../../generated/openapi-contract").PayinPartsState;
    workflowKind: string;
    appealMessage: string;
    orderId: string;
    /**
     * Активный платёж спорной заявки — или `null`, если его сейчас нет.
     *
     * Пусто это не по недосмотру: спор стал меткой ЗАЯВКИ и переживает возврат
     * в пул, а в пуле у заявки платежа нет вовсе. Ради такой строки спор с
     * платежа и переезжал — до правки её не видел никто. Все три исхода разбора
     * работают по платежу, поэтому по такой строке админ не может сделать
     * ничего, и интерфейс обязан это показать, а не предложить нажать.
     */
    paymentId: string | null;
    merchantPortalIn?: boolean;
    merchantPortalReceiptReady?: boolean;
    remoteId: string;
    merchantName: string;
    /** Держатель платежа; `null` у заявки, лежащей в пуле без платежа. */
    traderName: string | null;
    /**
     * Источник метки: `trader` — нажал человек, `claim_timeout` — вышел срок
     * брони по заявке кабинета, `external_source` — заявку кабинета больше
     * нельзя провести. У строки без платежа он единственное, что отвечает на
     * вопрос «почему заявка здесь»: трейдера в ней нет.
     */
    appealReason: string;
    amount: number;
    amountRemaining: number;
    paymentsMade: number;
    maxPayments: number;
    currency: string;
    currencyScale: number;
    appealedAt: string;
    receiptId: string | null;
    /**
     * Все файлы, ЗАГРУЖЕННЫЕ к спорному платежу, в порядке загрузки.
     *
     * Именно загруженные, а не предъявленные: предъявленного набора у
     * незакрытого платежа не существует — он появляется тем действием, которым
     * платёж закрывают. Одиночного `receiptId` для разбора не хватало: он
     * показывает последний загруженный файл, а трейдер, заплативший тремя
     * переводами, приложил три.
     */
    uploadedReceipts: DisputeReceipt[] | null;
    /** Курс последнего успешного платежа заявки — подставляется в поле ввода. */
    defaultRate: string | null;
    /**
     * Курс, зафиксированный при создании заявки (null у обычных заявок). Если
     * задан — бэк проведёт спор по нему, что бы админ ни отправил, поэтому поле
     * курса показываем заблокированным. Отдельно от defaultRate: спор по ПЕРВОМУ
     * платежу фикс-заявки успешных платежей ещё не имеет, а курс уже известен.
     */
    fixedRate: string | null;
    /**
     * Источник заявки кабинета (`paylord`), null у обычной заявки. Нужен окну
     * подтверждения: у внешней заявки исход «неуспех» отправляет отмену на
     * площадку, и админ обязан узнать об этом до нажатия, а не после.
     */
    externalSource: string | null;
    /**
     * Последний известный статус карточки у площадки (`pending`, `processing`,
     * `completed`, `cancelled`, `unclaimed`), null у обычной заявки. В работе
     * карточка только в первых двух состояниях; в любом другом площадка закрыла
     * заявку сама, наружу не уйдёт ничего, и решение админа — чисто денежное.
     */
    externalStatus: string | null;
    /**
     * Сигналы клиента и USDT-этап заявки-обмена; отсутствует у любой заявки
     * другого сценария.
     *
     * Поле не украшение карточки. По заявке-обмену клиент мог уже отправить
     * рубли, и оба «отрицательных» исхода — возврат в пул и неуспех — оставляют
     * его деньги без адресата. Бэкенд здесь ничего не запрещает: разбор спора
     * по построению остаётся человеку. Поэтому единственное, что отделяет
     * админа от необратимой ошибки, — то, что он видит ДО нажатия.
     */
    exchange?: AppealExchangeState;
}

export interface AppealExchangeState {
    clientMarkedPaidAt?: string;
    /** Сколько чеков прислал клиент. Число, а не список: файлы админ не открывает. */
    clientReceiptCount: number;
    payoutStatus: string;
}

export type ResolveDisputeOutcome = "success" | "return_to_pool" | "fail";

export interface ResolveDisputeRequest {
    outcome: ResolveDisputeOutcome;
    fiatAmountReal?: number;
    currencyRate?: string;
    /**
     * Чеки, которые админ приложил этим разбором, в порядке приложения.
     *
     * Значимы только при исходе `success`. Без них платёж закрылся бы
     * последним загруженным файлом, и на площадку уехал бы один чек из
     * нескольких — а перевод клиенту мог идти несколькими суммами. Список
     * необязателен: пусто — закрываем тем чеком, что уже есть у платежа.
     *
     * Несколько чеков принимаются только по заявкам кабинета; у обычной заявки
     * мерчанта чек один, и сервер откажет с `400`.
     */
    receiptIds?: string[];
}

export function listAppeals(token: string | null): Promise<AppealItem[]> {
    return apiFetch<AppealItem[]>("/payments/appeals", token);
}

/**
 * POST /v1/payments/{paymentId}/appeal-receipt — admin прикладывает чек (PDF) к
 * спорной заявке перед закрытием в успех. Аналог трейдерского uploadReceipt, но
 * admin-only и поверх платежа в статусе `appeal`. Реализация — в client.ts
 * (multipart, без Content-Type: application/json).
 */
export function uploadDisputeReceipt(
    token: string | null,
    paymentId: string,
    file: File,
): Promise<ReceiptUploadResponse> {
    return uploadAppealReceipt(paymentId, file, token);
}

export function resolveDispute(
    token: string | null,
    paymentId: string,
    body: ResolveDisputeRequest,
): Promise<OutPaymentResponse> {
    return apiFetch<OutPaymentResponse>(`/payments/${encodeURIComponent(paymentId)}/resolve`, token, {
        method: "POST",
        body: JSON.stringify(body),
    });
}

/** Ответ заявочного разбора: платежа у него нет, поэтому и карточки платежа нет. */
export interface ResolveOrderResponse {
    orderId: string;
    status: string;
}

/**
 * POST /v1/orders/{orderId}/dispute-payment — админ открывает платёж на
 * ВЫБРАННОГО трейдера по спорной заявке, у которой держателя нет (В-34).
 *
 * Первый из трёх шагов закрытия в успех: платёж → чек → закрытие. Одним
 * запросом это не делается и не может: закрытие требует чек, чек привязан к
 * платежу и грузится отдельной multipart-ручкой по paymentId — до создания
 * платежа прикладывать его не к чему.
 *
 * Ответ — обычная карточка платежа, и `id` в ней это paymentId, которым дальше
 * идут загрузка чека и разбор.
 */
export function openDisputePayment(
    token: string | null,
    orderId: string,
    traderId: string,
): Promise<OutPaymentResponse> {
    return apiFetch<OutPaymentResponse>(`/orders/${encodeURIComponent(orderId)}/dispute-payment`, token, {
        method: "POST",
        body: JSON.stringify({traderId}),
    });
}

/**
 * POST /v1/orders/{orderId}/resolve — разбор спора по заявке, активного платежа
 * у которой нет. Исход ровно один — `fail`: успех требует чека, а чек
 * принадлежит платежу, поэтому он идёт через openDisputePayment; «вернуть в
 * пул» бессмысленно — заявка и так в пуле.
 *
 * Ключ — заявка, а не платёж, по той же причине, по которой она сюда попала.
 */
export function resolveOrderDispute(
    token: string | null,
    orderId: string,
): Promise<ResolveOrderResponse> {
    return apiFetch<ResolveOrderResponse>(`/orders/${encodeURIComponent(orderId)}/resolve`, token, {
        method: "POST",
        body: JSON.stringify({outcome: "fail"}),
    });
}
